<template>
  <v-card class="d-flex flex-column">
    <template #prepend> <ticket-type-badge :ticket-type="ticketType" hide-text /> </template>
    <template #title>{{ $t(`${ticketType.toLowerCase()}s.create`) }}</template>
    <template v-if="ticketAsset" #subtitle>
      <span v-if="organizationId && isLoggedInOnSupplier">{{ organizationStore.getOrganizationDescription(organizationId) }} - </span>
      <span :title="`${ticketAsset.key} | ${ticketAsset.description}`">{{ ticketAsset.key }} | {{ ticketAsset.description }}</span>
    </template>

    <v-form
      v-model="valid"
      :loading="ticketAssetStatus === 'pending'"
      class="d-flex flex-column flex-grow-1 overflow-hidden"
      @submit.prevent="() => onSubmit()"
    >
      <v-card-text class="py-0 flex-grow-1 overflow-y-auto">
        <floating-card-header :title="$t('general')" icon="$ticket" />
        <v-row density="compact">
          <v-col cols="12" :sm="showInspectionTypes && !!formData.data ? 6 : 12" class="text-body-medium">
            <ticket-status-select v-model="formData.status" :statuses="ticketCreationStatuses" label="tickets.field.status" required />
          </v-col>

          <v-col v-if="showInspectionTypes && !!formData.data" cols="12" sm="6" class="text-body-medium">
            <div>{{ $t("inspections.field.inspectionType") }} *</div>
            <v-select
              v-model="(formData as Inspection).data.inspectionType"
              :items="inspectionTypes!"
              :loading="typesPending"
              :rules="[(val) => !!val || $t('validation.required')]"
              class="mb-2"
              item-title="description"
              item-value="description"
            />
          </v-col>
        </v-row>

        <ticket-general-form v-model="formData" :ticket-type="ticketType" :show-asset-field="!assetId" />
      </v-card-text>

      <v-card-actions class="flex-wrap justify-end border-t-sm">
        <v-btn variant="text" color="secondary" class="custom-hover-secondary" @click="() => onReset()">
          {{ $t("cancel") }}
        </v-btn>
        <v-btn variant="outlined" color="secondary" class="custom-hover-secondary" :loading="saving" type="submit">
          {{ $t("save") }}
        </v-btn>
        <v-btn
          variant="flat"
          color="primary"
          class="custom-hover-primary"
          :loading="saving"
          type="submit"
          @click="() => (redirectAfterSave = true)"
        >
          {{ $t("saveAndOpen") }}
        </v-btn>
      </v-card-actions>
    </v-form>
  </v-card>
</template>

<script setup lang="ts">
const emit = defineEmits<{
  (e: "ok", newTicketId: string): void
  (e: "cancel"): void
}>()

const props = defineProps<{ assetId?: string; ticketType: TicketType; organizationId?: string }>()
const { assetId, organizationId, ticketType } = toRefs(props)

const $i18n = useI18n()
const $router = useRouter()

const mainStore = useMainStore()
const inspectionStore = useInspectionStore()
const authStore = useAuthStore()
const organizationStore = useOrganizationStore()
const ticketStore = useTicketStore()
const supplierStore = useSupplierStore()

const { currentOrganization, isSupplier, sourceOrganization } = storeToRefs(authStore)
const { isLoggedInOnSupplier } = storeToRefs(supplierStore)

const { data: inspectionTypes, pending: typesPending } = useLazyAsyncData(
  "inspection-types",
  async () => {
    if (ticketType.value !== TicketType.INSPECTION) return []

    return await inspectionStore.getTypes()
  },
  { default: () => [], watch: [ticketType] },
)

const { data: ticketAsset, status: ticketAssetStatus } = useLazyAsyncData(
  `asset-ticket-${assetId?.value}`,
  async () => {
    if (!assetId.value) {
      return undefined
    }

    return ticketStore.getTicketAssetById(assetId.value)
  },
  { default: () => undefined },
)

const valid = ref<boolean>()
const saving = ref(false)
const redirectAfterSave = ref(false)

const formData = ref<Partial<Ticket>>({
  type: ticketType.value,
  asset: ticketAsset.value,
  organization: organizationId?.value ?? (isLoggedInOnSupplier.value ? undefined : currentOrganization.value),
  status: "JOB_READY",
  data: ticketType.value === TicketType.INSPECTION ? { inspectionType: "BRL" } : {},
} as Partial<Ticket>)

const showInspectionTypes = computed(
  () => ticketType.value === TicketType.INSPECTION && inspectionTypes.value?.filter((type) => type.description !== "BRL").length > 0,
)

const onSubmit = () => {
  if (!valid.value) {
    return
  }

  useExplicitSave(mainStore.notify, $i18n, async () => {
    saving.value = true

    try {
      const ticketData: CreateTicketPayload = {
        type: ticketType.value,
        asset: formData.value.asset?._id ?? assetId.value!,
        date: formData.value.date ? new Date(formData.value.date).toISOString() : undefined,
        description: formData.value.description!,
        supplier: formData.value.supplier?._id,
        users: formData.value.users?.map((user) => user._id) ?? [],
        contract: formData.value.contract?._id,
        remark: formData.value.remark,
        invoicePeriod: formData.value.invoicePeriod ?? undefined, // can be null (when user cleared the select), but backend expects undefined if not provided
        status: formData.value.status!,
        tags: formData.value.tags?.map((tag) => tag._id) ?? [],
        startAt: formData.value.startAt ? new Date(formData.value.startAt).toISOString() : undefined,
        endAt: formData.value.endAt ? new Date(formData.value.endAt).toISOString() : undefined,
        data: formData.value.data
          ? {
              ...("inspectionType" in formData.value.data ? { inspectionType: formData.value.data.inspectionType } : {}),
              ...("approved" in formData.value.data ? { approved: formData.value.data.approved } : {}),
              ...("cause" in formData.value.data ? { cause: formData.value.data.cause?._id } : {}),
              ...("settlement" in formData.value.data ? { settlement: formData.value.data.settlement } : {}),
              ...("solution" in formData.value.data ? { solution: formData.value.data.solution?._id } : {}),
              ...("component" in formData.value.data ? { component: formData.value.data.component?._id } : {}),
              ...("componentGroup" in formData.value.data ? { componentGroup: formData.value.data.componentGroup?._id } : {}),
              ...("nextInspectionAt" in formData.value.data ? { nextInspectionAt: formData.value.data.nextInspectionAt } : {}),
              ...("remark" in formData.value.data ? { remark: formData.value.data.remark } : {}),
              ...("reporter" in formData.value.data ? { reporter: formData.value.data.reporter } : {}),
              ...("shouldReinspect" in formData.value.data ? { shouldReinspect: formData.value.data.shouldReinspect } : {}),
              ...("shouldRepair" in formData.value.data ? { shouldRepair: formData.value.data.shouldRepair } : {}),
            }
          : undefined,
      }

      const newTicket = await ticketStore.createOne(ticketData)

      if (redirectAfterSave.value) {
        $router.push(`/tickets/${ticketType.value.toLowerCase()}s/${newTicket._id}`)
      }

      saving.value = false
      emit("ok", newTicket._id)
    } catch (error) {
      saving.value = false
      console.error(error)
      throw error
    }
  })
}

const onReset = () => emit("cancel")

watch(
  [isSupplier, sourceOrganization],
  async () => {
    if (formData.value.supplier) {
      return
    }

    if (isSupplier.value && sourceOrganization.value) {
      const { _id, description } = await organizationStore.getOrganization(sourceOrganization.value)
      formData.value.supplier = { _id, description }
    }
  },
  { immediate: true },
)
</script>
